import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SEO_CONFIG } from '../utils/seo';

// Import translations directly for SEO
import en from '../locales/en.json';
import fr from '../locales/fr.json';
import es from '../locales/es.json';
import de from '../locales/de.json';

const seoTranslations = { en, fr, es, de };

/**
 * SEOHead Component - Multilingual SEO meta tags
 */
const SEOHead = ({ title, description, keywords, ogImage, ogType = 'website', canonicalUrl, structuredData }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const lastLangRef = useRef(null);
  const propsRef = useRef({ title, description, keywords, ogImage, ogType, canonicalUrl, structuredData });

  // Keep props ref updated
  propsRef.current = { title, description, keywords, ogImage, ogType, canonicalUrl, structuredData };

  // Get translation directly from imported resources
  const getTranslation = (langCode, key) => {
    const lang = (langCode || 'en').split('-')[0];
    const translations = seoTranslations[lang] || seoTranslations.en;
    const keys = key.split('.');
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
    }
    return typeof value === 'string' ? value : null;
  };

  // Update meta tags function
  const updateMetaTags = (currentLang) => {
    const { title, description, keywords, ogImage, ogType, canonicalUrl, structuredData } = propsRef.current;
    const lang = (currentLang || 'en').split('-')[0];
    const pagePath = location.pathname;
    
    // Determine page key
    let pageKey = 'home';
    if (pagePath.includes('/events')) pageKey = 'events';
    else if (pagePath.includes('/exhibitors')) pageKey = 'exhibitors';
    else if (pagePath.includes('/calendar')) pageKey = 'calendar';
    else if (pagePath.includes('/about')) pageKey = 'about';
    else if (pagePath.includes('/contact')) pageKey = 'contact';
    else if (pagePath.includes('/booth')) pageKey = 'booth';
    else if (pagePath.includes('/sitemap')) pageKey = 'sitemap';
    else if (pagePath.includes('/privacy')) pageKey = 'privacy';
    else if (pagePath.includes('/terms')) pageKey = 'terms';

    // Get SEO content
    const getSEO = (field) => {
      return getTranslation(lang, `seo.${pageKey}.${field}`) 
          || getTranslation(lang, `seo.default.${field}`)
          || (field === 'title' ? SEO_CONFIG.defaultTitle : 
              field === 'description' ? SEO_CONFIG.defaultDescription : 
              SEO_CONFIG.defaultKeywords);
    };

    const seoTitle = title || getSEO('title');
    const seoDescription = description || getSEO('description');
    const seoKeywords = keywords || getSEO('keywords');

    // Update DOM
    document.documentElement.lang = lang;
    document.title = seoTitle;

    // Helper to update meta
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', seoDescription);
    setMeta('keywords', seoKeywords);
    setMeta('language', lang);
    setMeta('og:title', seoTitle, true);
    setMeta('og:description', seoDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', ogImage || SEO_CONFIG.ogImage, true);
    setMeta('og:site_name', SEO_CONFIG.siteName, true);
    setMeta('og:locale', { en: 'en_US', fr: 'fr_FR', es: 'es_ES', de: 'de_DE' }[lang] || 'en_US', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', seoTitle);
    setMeta('twitter:description', seoDescription);
    setMeta('twitter:image', ogImage || SEO_CONFIG.ogImage);

    // Canonical - ALWAYS points to the default (English) URL without language query params
    // This fixes "Alternate page with proper canonical tag" GSC issue
    const canonicalHref = canonicalUrl || `${SEO_CONFIG.siteUrl}${pagePath}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalHref;

    // Update hreflang
    ['en', 'fr', 'es', 'de'].forEach(l => {
      const link = document.querySelector(`link[hreflang="${l}"]`);
      if (link) link.href = `https://tradehub24.co${pagePath}${l !== 'en' ? `?lang=${l}` : ''}`;
    });
    const xDefault = document.querySelector('link[hreflang="x-default"]');
    if (xDefault) xDefault.href = `https://tradehub24.co${pagePath}`;

    // Structured data
    if (structuredData) {
      let script = document.querySelector('script[data-seo-structured]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.seoStructured = 'true';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  };

  // PostHog SEO Analytics tracking
  const trackSEOEvent = (eventName, properties) => {
    if (typeof window !== 'undefined' && window.posthog) {
      window.posthog.capture(eventName, properties);
    }
  };

  // Main effect for language changes and initial load
  useEffect(() => {
    const handler = (lng) => {
      const normalizedLang = (lng || 'en').split('-')[0];
      const previousLang = lastLangRef.current;
      lastLangRef.current = normalizedLang;
      updateMetaTags(normalizedLang);
      
      // Track language change event in PostHog
      if (previousLang && previousLang !== normalizedLang) {
        trackSEOEvent('language_changed', {
          from_language: previousLang,
          to_language: normalizedLang,
          page_path: location.pathname,
          page_title: document.title
        });
      }
    };
    
    i18n.on('languageChanged', handler);
    
    // Initial update
    const initialLang = (i18n.language || 'en').split('-')[0];
    updateMetaTags(initialLang);
    lastLangRef.current = initialLang;
    
    // Track initial page view with language
    trackSEOEvent('seo_page_view', {
      language: initialLang,
      page_path: location.pathname,
      page_title: document.title
    });

    return () => i18n.off('languageChanged', handler);
  }, [i18n, location.pathname]);

  // Update on props change
  useEffect(() => {
    updateMetaTags(lastLangRef.current || i18n.language);
  }, [title, description, keywords, ogImage, ogType, canonicalUrl, structuredData, i18n.language]);

  return null;
};

export default SEOHead;
